class ProductMedia extends HTMLElement {
  constructor() {
    super();
    this.swiperLoaded = false;
    this.initialized = false;
  }

  // Function to dynamically load Swiper
  async loadSwiper() {
    if (typeof window.Swiper !== "undefined") {
      this.swiperLoaded = true;
      return Promise.resolve();
    }

    if (this.swiperLoading) {
      return this.swiperLoading;
    }

    try {
      this.swiperLoading = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = window.swiperUrl;
        script.async = true;

        script.onload = () => {
          this.swiperLoaded = true;
          this.swiperLoading = null;
          resolve();
        };
        script.onerror = () => {
          console.error("Failed to load Swiper.js"); // eslint-disable-line no-console -- surfaces a genuine asset-load failure to the browser console
          this.swiperLoading = null;
          reject(new Error("Swiper loading failed"));
        };
        document.head.appendChild(script);
      });

      return this.swiperLoading;
    } catch (error) {
      console.error("Error loading Swiper:", error); // eslint-disable-line no-console -- surfaces a genuine asset-load failure to the browser console
      this.swiperLoading = null;
      throw error;
    }
  }

  // Idle loading - load during browser idle time
  static idleLoadSwiper() {
    const hasProductMedia = document.querySelector("product-media");
    if (!hasProductMedia) return;

    const loadSwiperIdle = () => {
      if (typeof window.Swiper !== "undefined") return;

      const script = document.createElement("script");
      script.src = window.swiperUrl;
      script.async = true;
      document.head.appendChild(script);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(loadSwiperIdle, { timeout: 2000 });
    } else {
      setTimeout(loadSwiperIdle, 1000);
    }
  }

  // connect component
  connectedCallback() {
    // Try immediate initialization first for critical above-the-fold content
    const rect = this.getBoundingClientRect();
    const isAboveFold = rect.top < window.innerHeight;

    if (isAboveFold) {
      this.init();
    } else {
      // Use Intersection Observer for below-the-fold content
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.initialized) {
              this.init();
              observer.unobserve(this);
            }
          });
        },
        {
          rootMargin: "100px", // Start loading 100px before element comes into view
        }
      );

      observer.observe(this);
    }
  }

  async init() {
    if (this.initialized) return;
    this.initialized = true;

    // spec 48 (F2): must match the CSS split in product.css (stacked grid from
    // 989px, slider up to 988.98px) — at 989–990px the old 990px query enabled
    // the slider (loop clones + wrapper transform) inside the stacked grid and
    // the gallery rendered as an empty box.
    const breakpoint = window.matchMedia("(max-width: 988.98px)");
    let productMediaSlider;
    let thumbsSwiper;
    let onClick;

    // spec 48 (F21/D10a, gate F29): the thumbnail rail keeps 52px clear zones
    // under the two arrows only when the tiles overflow the rail. When every
    // tile fits between the arrows the offsets are 0, otherwise Swiper's
    // `centerInsufficientSlides` would centre the group inside the offset box
    // and push the last tile under the next arrow. Re-evaluated on resize.
    const THUMB_WIDTH = 76;
    const THUMB_GAP = 8;
    const THUMB_CLEAR = 52;
    const railOffset = (railWidth, tileCount) => {
      const tilesWidth = tileCount * THUMB_WIDTH + Math.max(0, tileCount - 1) * THUMB_GAP;
      return tilesWidth <= railWidth - 2 * THUMB_CLEAR ? 0 : THUMB_CLEAR;
    };

    // spec 48 (owner report 2026-09-04, gate F27): the thumbnail rail
    // occasionally stayed "grabbed" after the mouse button was released. On a
    // mouse, Swiper 8 ends a drag only on `pointerup`; a release it never sees
    // (outside the window, over an iframe, a lost pointer) leaves `isTouched`
    // set and the rail follows the cursor. A mouse move with no button held is
    // proof the drag is over — end it. Registered once per element (init runs
    // once); the handlers read the current slider instances.
    const endLingeringDrag = (event) => {
      [productMediaSlider, thumbsSwiper].forEach((instance) => {
        if (instance && !instance.destroyed && instance.touchEventsData.isTouched) {
          // the event is a button-less move, not a release over a tile: with
          // allowClick left true Swiper would read its target as a clicked slide
          // and emit a phantom tap (Swiper restores allowClick on its next tick)
          instance.allowClick = false;
          instance.onTouchEnd(event);
        }
      });
    };
    document.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerType === "mouse" && event.buttons === 0) endLingeringDrag(event);
      },
      { passive: true }
    );
    window.addEventListener("blur", () => {
      if (typeof window.PointerEvent !== "function") return;
      endLingeringDrag(new window.PointerEvent("pointerup", { pointerType: "mouse" }));
    });

    const checkBreakpoint = () => {
      // gate F32: this component's own galleries only — the slider refs and
      // the `enabling` flag are per element, so a document-wide query would
      // let one <product-media> mount, skip or destroy another's slider.
      const allSliders = this.querySelectorAll(".swiper-product-media");

      allSliders.forEach((slider) => {
        if (slider.dataset.desktopLayout === "slider") {
          this.enableSlider(slider);
        } else {
          // display in stacked format on desktop
          if (breakpoint.matches === false) {
            this.disableSlider(slider);
            // display in slider format on mobile
          } else if (breakpoint.matches === true) {
            if (!slider.classList.contains("swiper-initialized")) {
              this.enableSlider(slider);
            }
          }
        }

        if (breakpoint.matches === false) {
          this.enableZoom();
          // don't enable zoom on mobile.
        } else if (breakpoint.matches === true) {
          this.disableZoom();
        }
      });
    };

    // gate F31: enableSlider is idempotent and race-safe. The desktop-slider
    // layout re-enters it on every breakpoint crossing, and a mobile→desktop
    // flip during the async Swiper load must not mount a slider into the
    // stacked grid.
    let enabling = false;
    this.enableSlider = async (slider) => {
      if (enabling) return;
      enabling = true;
      try {
        // Load Swiper if not already loaded
        await this.loadSwiper();

        // Verify Swiper is available
        if (typeof window.Swiper === "undefined") {
          throw new Error("Swiper failed to load");
        }

        if (slider.dataset.desktopLayout !== "slider" && !breakpoint.matches) return;
        if (productMediaSlider && !productMediaSlider.destroyed && slider.classList.contains("swiper-initialized")) {
          return;
        }
        if (productMediaSlider && !productMediaSlider.destroyed) {
          productMediaSlider.destroy(true, true);
        }
        productMediaSlider = undefined;

        const sliderId = slider.id;

        // Validate slider element
        if (!slider || !sliderId) {
          console.error("Invalid slider element or missing ID"); // eslint-disable-line no-console -- surfaces a genuine malformed-DOM condition
          return;
        }

        slider.classList.remove("product-grid");
        const slideElements = slider.querySelectorAll(".swiper-slide:not(.swiper-slide-duplicate)");
        const slideCount = slideElements.length;

        // Check wrapper element
        const wrapper = slider.querySelector(".swiper-wrapper");

        if (!wrapper) {
          console.error("No .swiper-wrapper found in slider"); // eslint-disable-line no-console -- surfaces a genuine malformed-DOM condition
          slider.classList.add("product-grid");
          return;
        }

        const thumbsId = `${sliderId}--thumbs`;
        const thumbsElement = document.getElementById(thumbsId);

        // gate F30: a rail left over from a previous slider mode would be
        // mounted twice; disableSlider destroys it, this is the belt.
        if (thumbsSwiper && !thumbsSwiper.destroyed) {
          thumbsSwiper.destroy(true, true);
        }
        thumbsSwiper = undefined;

        if (thumbsElement) {
          const thumbsSlides = thumbsElement.querySelectorAll(".swiper-slide");
          const thumbsWrapper = thumbsElement.querySelector(".swiper-wrapper");

          if (thumbsSlides.length > 0 && thumbsWrapper) {
            try {
              // spec 48 (F21/D10a): fixed 76px tiles (assets/product.css), as many
              // per view as fit, clear zones under the arrows at both ends only
              // when the tiles overflow (railOffset); few thumbs centre instead of
              // scrolling. Mouse drag on the rail is Swiper's own (owner
              // 2026-09-04: keep it); what F23 blocks is the browser's native
              // image drag + selection, via draggable=false and CSS on the images.
              const initialOffset = railOffset(thumbsElement.clientWidth, thumbsSlides.length);
              thumbsSwiper = new window.Swiper(`#${thumbsId}`, {
                loop: false,
                slidesPerView: "auto",
                spaceBetween: THUMB_GAP,
                slidesOffsetBefore: initialOffset,
                slidesOffsetAfter: initialOffset,
                // centre only when everything fits between the arrows; otherwise
                // Swiper would centre a group that fits the rail but not the
                // arrow-free zone and park the last tile under the next arrow
                centerInsufficientSlides: initialOffset === 0,
                slideToClickedSlide: true,
                watchSlidesProgress: true,
                // a mouse click that wobbles 1px was read as a drag and swallowed
                threshold: 8,
                // Swiper's overflow check locks the rail (no drag, no slideTo)
                // whenever the tiles fit the rail minus ONE offset, while
                // railOffset turns the offsets on when they don't fit minus TWO —
                // in that 52px band (3 tiles on a 360–375px phone, 4 on 430px)
                // the last tile parked under the next arrow and could not be
                // dragged out. snapGrid still bounds the travel.
                watchOverflow: false,
                on: {
                  resize(rail) {
                    const offset = railOffset(rail.width, rail.slides.length);
                    if (offset !== rail.params.slidesOffsetBefore) {
                      rail.params.slidesOffsetBefore = offset;
                      rail.params.slidesOffsetAfter = offset;
                      rail.params.centerInsufficientSlides = offset === 0;
                      rail.update();
                    }
                  },
                },
              });
            } catch (thumbsError) {
              console.error("Failed to create thumbs swiper:", thumbsError); // eslint-disable-line no-console -- surfaces a genuine Swiper init failure; execution continues without thumbs
              // Continue without thumbs swiper
            }
          }
        }

        if (slideCount > 1) {
          const config = {
            // spec 48 (F22, owner 2026-09-04): no loop — arrows disable at the ends.
            loop: false,
            threshold: 8,
            zoom: {
              maxRatio: 5,
              containerClass: `swiper-zoom-container`,
            },
            pagination: {
              el: `#${sliderId} .swiper-pagination`,
              clickable: true,
            },
            navigation: {
              nextEl: `#${sliderId} .swiper-button-next`,
              prevEl: `#${sliderId} .swiper-button-prev`,
            },
          };

          // Only add thumbs if we have thumbsSwiper
          if (thumbsSwiper) {
            config.thumbs = {
              swiper: thumbsSwiper,
            };
            // spec 48 (F21): Swiper's own thumbs sync treats a half-visible
            // tile as visible, so the active thumb could end up cut by the
            // rail edge. Scroll the rail by the smallest amount that shows the
            // whole tile between the two arrow zones.
            config.on = {
              slideChange(main) {
                const ts = thumbsSwiper;
                const i = main.activeIndex;
                // Swiper's thumbs module reacts to the same event and may snap the
                // rail to a slide first; run after it so the last word is ours.
                window.requestAnimationFrame(() => {
                  if (!ts || ts.destroyed || !ts.slides || !ts.slides[i]) return;
                  // Nothing to scroll when every tile already fits (centred rail).
                  if (ts.maxTranslate() >= ts.minTranslate()) return;
                  // Measure the tile in the DOM (Swiper's grid shifts when tiles are
                  // centred) and project it to where the rail is heading: the
                  // rendered transform may still be mid-transition while
                  // `translate` already holds the target.
                  const before = ts.params.slidesOffsetBefore || 0;
                  const after = ts.params.slidesOffsetAfter || 0;
                  const railRect = ts.el.getBoundingClientRect();
                  const tileRect = ts.slides[i].getBoundingClientRect();
                  const drift = ts.translate - ts.getTranslate();
                  const left = tileRect.left - railRect.left + drift;
                  const right = tileRect.right - railRect.left + drift;
                  let delta = 0;
                  if (left < before) delta = before - left;
                  else if (right > ts.width - after) delta = ts.width - after - right;
                  if (!delta) return;
                  const target = Math.max(ts.maxTranslate(), Math.min(ts.minTranslate(), ts.translate + delta));
                  if (Math.abs(target - ts.translate) < 0.5) return;
                  ts.translateTo(target, ts.params.speed, true, true);
                });
              },
            };
          }

          productMediaSlider = new window.Swiper(`#${sliderId}`, config);
        }
      } catch (error) {
        console.error("Failed to initialize Swiper:", error); // eslint-disable-line no-console -- surfaces a genuine Swiper init failure before the grid-layout fallback

        // Fallback to grid layout if Swiper fails to load
        slider.classList.add("product-grid");
      } finally {
        enabling = false;
      }
    };

    this.disableSlider = () => {
      if (productMediaSlider !== undefined) {
        // destroy() strips inline styles from the slides only; the zoom module's
        // scale on the img and translate on the zoom container are descendants
        // and would survive into the stacked grid (iPad: double-tap zoom, rotate)
        if (productMediaSlider.zoom && productMediaSlider.zoom.scale !== 1) productMediaSlider.zoom.out();
        productMediaSlider.destroy(true, true);
        productMediaSlider = undefined;
      }
      // gate F30: the thumbs instance is passed to the main slider as an
      // instance, so Swiper does not destroy it with the main one; left alive it
      // was mounted twice after a 989px round trip (iPad rotation) and its stale
      // tap handler threw — and on touch there is no button-less move to heal it.
      if (thumbsSwiper !== undefined) {
        if (!thumbsSwiper.destroyed) thumbsSwiper.destroy(true, true);
        thumbsSwiper = undefined;
      }
    };

    this.enableZoom = () => {
      const mainProduct = document.getElementById("MainProduct");
      if (mainProduct) {
        mainProduct.classList.add("product-grid");
      }

      const slides = document.getElementsByClassName("swiper-zoom-container");
      Array.from(slides).forEach((slide) => {
        const onMouseMove = function (event) {
          const x = (event.offsetX / slide.offsetWidth) * 100;
          const y = (event.offsetY / slide.offsetHeight) * 100;
          slide.style.backgroundPosition = x + "% " + y + "%";
        };
        // remove zoom
        const onMouseOut = function () {
          slide.removeEventListener("mousemove", onMouseMove);
          slide.removeEventListener("mouseout", onMouseOut);
          slide.classList.remove("js-zoom-container");
          slide.style.backgroundImage = "";
        };
        const onClick = function () {
          const imageUrl = slide.children[0].src;
          slide.style.backgroundImage = `url(${imageUrl})`;
          slide.classList.add("js-zoom-container");
          slide.addEventListener("mousemove", onMouseMove);
          slide.addEventListener("mouseout", onMouseOut);
        };
        slide.addEventListener("click", onClick);
      });
    };

    this.disableZoom = () => {
      const slides = document.getElementsByClassName("swiper-zoom-container");
      Array.from(slides).forEach((slide) => {
        slide.removeEventListener("click", onClick);
      });
    };

    breakpoint.addListener(checkBreakpoint);
    checkBreakpoint(); // first check
  }
}

// Initialize idle loading when page loads
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    ProductMedia.idleLoadSwiper();
  });
} else {
  ProductMedia.idleLoadSwiper();
}

customElements.define("product-media", ProductMedia);
